import type { PublicDriver, PublicReadingResult, PublicTruck, SubmitReadingInput } from './types';

/**
 * Public mileage API (company trucks, 2026-09-10). Plain `fetch`, no portal
 * session, no auth headers — the same style as SmsOptInPage and the
 * my-schedule week link. The shared axios instance is deliberately NOT used:
 * it carries the session interceptors, and a driver on the road has none.
 *
 * Contract: docs/superpowers/plans/2026-09-10-company-trucks.md
 *   GET  /api/v1/public/mileage/drivers          -> { data: [{ name }] }
 *   GET  /api/v1/public/mileage/trucks?driver=   -> { data: [{ id, asset_number, name, mileage }] }  (no plate: trucks low-fix pass 2026-09-10)
 *   POST /api/v1/public/mileage/readings         -> 201 { data: { reading, read_at, truck } }
 *                                                 | 422 { error: { code: 'READING_TOO_LOW', details: { latest } } }
 *                                                 | 422 { error: { code: 'READING_TOO_HIGH', details: { latest, max, confirmable, last_read_at } } }  (trucks fix pass 2026-09-10)
 *
 * 2026-10-07: `confirmable` is true when resending the same reading with
 * confirm_large_jump would be accepted; `last_read_at` is the ISO timestamp
 * of the reading the jump was measured from (null when the truck has none).
 */

const BASE = '/api/v1/public/mileage';

export class PublicMileageError extends Error {
  status: number;
  code: string | null;
  details: Record<string, unknown> | null;

  constructor(status: number, code: string | null, details: Record<string, unknown> | null, message?: string) {
    super(message ?? `Public mileage request failed (${status})`);
    this.name = 'PublicMileageError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

interface ErrorEnvelope {
  error?: { code?: string; message?: string; details?: Record<string, unknown> };
}

async function readJson<T>(r: Response): Promise<T | null> {
  return r.json().catch(() => null) as Promise<T | null>;
}

async function failFrom(r: Response): Promise<never> {
  const body = await readJson<ErrorEnvelope>(r);
  throw new PublicMileageError(
    r.status,
    body?.error?.code ?? null,
    body?.error?.details ?? null,
    body?.error?.message,
  );
}

export async function fetchDrivers(): Promise<PublicDriver[]> {
  const r = await fetch(`${BASE}/drivers`, { headers: { Accept: 'application/json' } });
  if (!r.ok) return failFrom(r);
  const body = await readJson<{ data?: PublicDriver[] }>(r);
  return body?.data ?? [];
}

export async function fetchTrucksForDriver(driver: string): Promise<PublicTruck[]> {
  const r = await fetch(`${BASE}/trucks?driver=${encodeURIComponent(driver)}`, {
    headers: { Accept: 'application/json' },
  });
  if (!r.ok) return failFrom(r);
  const body = await readJson<{ data?: PublicTruck[] }>(r);
  return body?.data ?? [];
}

/**
 * JSON when there is no photo; multipart when there is. The multipart
 * request sets NO Content-Type so the browser writes the boundary itself.
 */
export async function submitReading(input: SubmitReadingInput): Promise<PublicReadingResult> {
  const { truck_id, driver, reading, note, photo, confirm_large_jump } = input;
  let r: Response;
  if (photo) {
    const form = new FormData();
    form.append('truck_id', truck_id);
    form.append('driver', driver);
    form.append('reading', String(reading));
    if (note) form.append('note', note);
    // Laravel's boolean rule takes '1' from a form body, not 'true'.
    if (confirm_large_jump) form.append('confirm_large_jump', '1');
    form.append('photo', photo, photo.name);
    r = await fetch(`${BASE}/readings`, { method: 'POST', headers: { Accept: 'application/json' }, body: form });
  } else {
    r = await fetch(`${BASE}/readings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        truck_id,
        driver,
        reading,
        note: note || null,
        ...(confirm_large_jump ? { confirm_large_jump: true } : {}),
      }),
    });
  }
  if (!r.ok) return failFrom(r);
  const body = await readJson<{ data?: PublicReadingResult }>(r);
  if (!body?.data) throw new PublicMileageError(r.status, null, null, 'Empty response');
  return body.data;
}
